import "./support.css";
import { useState } from "react";
import {
  Board,
  BoardDotStatus,
  buildFilterObject,
  Edit,
  EditSelectionItemProps,
  exportTableToCSV,
  Modal,
  OperationArea,
  Panel,
  StringMap,
} from "../../components";
import { useAuth, useLanguage } from "../../hooks";
import { SupportFilters, SupportIncidents } from "./components";
import { resetObjectValues } from "../../utils";
import { SupportServices } from "../../services";
import {
  AdminIncidentProps,
  getIncidentStatusColors,
  INCIDENT_STATUSES,
  INCIDENT_TYPES,
  IncidentStatusType,
} from "./support.props";

enum SupportCreateIncidentEnum {
  TYPE = "type",
  NAME = "name",
  EMAIL = "email",
  PHONE = "phone",
  DESCRIPTION = "description",
}

const emptyCreateForm = {
  type: "",
  name: "",
  email: "",
  phone: "",
  description: "",
};

const emptyRespondForm = { status: "" as IncidentStatusType | "", message: "" };

const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  return isNaN(date.getTime()) ? iso : date.toLocaleString("es-ES");
};

const SupportScreen: React.FC = () => {
  const { t } = useLanguage();
  const { rol, user } = useAuth();
  const isAdmin = rol.includes("admin");

  const [showModalCreateIncident, setShowModalCreateIncident] =
    useState<boolean>(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [creating, setCreating] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string>("");
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const [search, setSearch] = useState<string>("");
  const [preFilters, setPreFilters] = useState<StringMap>({});
  const [filters, setFilters] = useState<StringMap>({});
  const [exportDisabled, setExportDisabled] = useState<boolean>(false);

  const [detail, setDetail] = useState<AdminIncidentProps | null>(null);
  const [respondForm, setRespondForm] = useState(emptyRespondForm);
  const [respondingSaving, setRespondingSaving] = useState<boolean>(false);
  const [respondError, setRespondError] = useState<string>("");
  const [replyMessage, setReplyMessage] = useState<string>("");

  const itemsIncidentType: EditSelectionItemProps[] = INCIDENT_TYPES.map(
    (key) => ({ key, text: t.supportScreen.table.types[key] })
  );
  const itemsStatus: EditSelectionItemProps[] = INCIDENT_STATUSES.map(
    (key) => ({ key, text: t.supportScreen.table.tbody[key] })
  );

  const deleteByKey = (key: string) => {
    setFilters({ ...filters, [key]: "" });
    setPreFilters({ ...preFilters, [key]: "" });
  };

  const handleCleanFilters = () => {
    setFilters(resetObjectValues(filters));
    setPreFilters(resetObjectValues(preFilters));
    setSearch("");
  };

  const openCreate = () => {
    setCreateForm({ ...emptyCreateForm, email: user ?? "" });
    setCreateError("");
    setShowModalCreateIncident(true);
  };

  const isCreateDisabled =
    createForm.type.trim().length === 0 ||
    createForm.name.trim().length === 0 ||
    createForm.email.trim().length === 0 ||
    createForm.description.trim().length === 0 ||
    creating;

  const handleCreate = async () => {
    setCreating(true);
    setCreateError("");

    const { createIncident } = SupportServices();
    const result = await createIncident({
      type: createForm.type,
      name: createForm.name,
      email: createForm.email,
      phone: createForm.phone || undefined,
      description: createForm.description,
    });

    setCreating(false);

    if (result.error || !result.data) {
      setCreateError(result.messageError);
      return;
    }

    setShowModalCreateIncident(false);
    setCreateForm(emptyCreateForm);
    setRefreshKey((key) => key + 1);
  };

  const openDetail = async (incident: AdminIncidentProps) => {
    setDetail(incident);
    setRespondForm({ status: incident.status, message: "" });
    setReplyMessage("");
    setRespondError("");

    // Abrir el ticket lo marca como leído para quien lo abre.
    if (incident.unread) {
      const { markIncidentRead } = SupportServices();
      const result = await markIncidentRead(incident.id, isAdmin);
      if (result.data) {
        setDetail(result.data);
        setRefreshKey((key) => key + 1);
      }
    }
  };

  // El solicitante contesta en su ticket mientras no esté completado.
  const canReply = !isAdmin && !!detail && detail.status !== "completed";

  const handleReply = async () => {
    if (!detail || replyMessage.trim().length === 0) {
      return;
    }

    setRespondingSaving(true);
    setRespondError("");

    const { replyIncident } = SupportServices();
    const result = await replyIncident(detail.id, replyMessage.trim());

    setRespondingSaving(false);

    if (result.error || !result.data) {
      setRespondError(result.messageError);
      return;
    }

    setDetail(result.data as AdminIncidentProps);
    setReplyMessage("");
    setRefreshKey((key) => key + 1);
  };

  const handleRespond = async () => {
    if (!detail) {
      return;
    }

    setRespondingSaving(true);
    setRespondError("");

    const { respondIncident } = SupportServices();
    const result = await respondIncident(detail.id, {
      status: respondForm.status || undefined,
      message: respondForm.message || undefined,
    });

    setRespondingSaving(false);

    if (result.error || !result.data) {
      setRespondError(result.messageError);
      return;
    }

    // Mantenemos el modal abierto: una incidencia puede recibir varias respuestas
    // seguidas (p.ej. "Respondida" y más tarde "Completada").
    setDetail(result.data);
    setRespondForm({ status: result.data.status, message: "" });
    setRefreshKey((key) => key + 1);
  };

  return (
    <>
      <Panel>
        <OperationArea
          title={t.supportScreen.title}
          filter={{
            show: true,
            filters: filters,
            pathFilterL: t.supportScreen.table.thead,
            deleteByKey,
            deleteAll: handleCleanFilters,
            modal: {
              content: (
                <SupportFilters
                  filters={preFilters}
                  setFilters={setPreFilters}
                />
              ),
              textButton: "",
              onOk() {
                setFilters(preFilters);
              },
              backdropClosable: false,
            },
          }}
          exportdata={{
            show: true,
            onClick: () =>
              exportTableToCSV("table-support", `${t.supportScreen.title}.csv`),
            text: t.components.operations.area.expordata,
            disabled: exportDisabled,
          }}
          search={{
            show: true,
            value: search,
            setValue: setSearch,
            placeholder: t.components.operations.area.search,
          }}
          toolbarButton={{
            show: true,
            text: t.supportScreen.createIncident,
            onClick: openCreate,
          }}
        >
          <Board id="table-support">
            <SupportIncidents
              key={refreshKey}
              search={search}
              filters={filters}
              setExportDisabled={setExportDisabled}
              isAdmin={isAdmin}
              onOpenDetail={openDetail}
            />
          </Board>

          <Modal
            open={showModalCreateIncident}
            setOpen={setShowModalCreateIncident}
            title={t.supportScreen.createIncident}
            actions={[
              {
                text: t.supportScreen.createIncident,
                onClick: handleCreate,
                disabled: isCreateDisabled,
              },
            ]}
          >
            <Edit>
              {/*  INCIDENT TYPE */}
              <Edit.Selection
                id={SupportCreateIncidentEnum.TYPE}
                placeholder={t.supportScreen.fields.incidentType}
                items={itemsIncidentType}
                selected={
                  itemsIncidentType.find(
                    (item) => item.key === createForm.type
                  ) ?? { key: "", text: "" }
                }
                onSelect={(item) =>
                  setCreateForm({ ...createForm, type: item.key })
                }
              />

              {/* CONTACT PHONE */}
              <Edit.Input
                id={SupportCreateIncidentEnum.PHONE}
                placeholder={t.supportScreen.fields.phone}
                value={createForm.phone}
                onChange={(e) =>
                  setCreateForm({ ...createForm, phone: e.target.value })
                }
              />

              {/* CONTACT EMAIL (el del usuario logado, no editable) */}
              <Edit.Input
                id={SupportCreateIncidentEnum.EMAIL}
                placeholder={t.supportScreen.fields.email}
                value={createForm.email}
                disabled
              />

              {/* TÍTULO (segunda línea, ancho completo) */}
              <Edit.Input
                id={SupportCreateIncidentEnum.NAME}
                placeholder={t.supportScreen.fields.name}
                value={createForm.name}
                onChange={(e) =>
                  setCreateForm({ ...createForm, name: e.target.value })
                }
              />

              {/* INCIDENT DESCRIPTION */}
              <Edit.TextArea
                id={SupportCreateIncidentEnum.DESCRIPTION}
                placeholder={t.supportScreen.fields.description}
                value={createForm.description}
                onChange={(e) =>
                  setCreateForm({ ...createForm, description: e.target.value })
                }
              />
              {createError && (
                <p className="support-form-error">{createError}</p>
              )}
            </Edit>
          </Modal>

          <Modal
            open={detail !== null}
            setOpen={(open) => !open && setDetail(null)}
            title={`${detail?.reference ?? ""} · ${detail?.name ?? ""}`}
            className="support-detail-modal"
            actions={
              isAdmin
                ? [
                    {
                      text: t.supportScreen.respond.save,
                      onClick: handleRespond,
                      disabled: respondingSaving,
                    },
                  ]
                : canReply
                ? [
                    {
                      text: t.supportScreen.respond.reply,
                      onClick: handleReply,
                      disabled:
                        respondingSaving || replyMessage.trim().length === 0,
                    },
                  ]
                : []
            }
          >
            <div className="support-detail-grid">
              <div className="support-detail-history">
                <p className="support-detail-description">
                  {detail?.description}
                </p>

                <h4 className="support-detail-subtitle">
                  {t.supportScreen.respond.historyTitle}
                </h4>

                {!detail?.responses.length ? (
                  <p className="support-detail-empty">
                    {t.supportScreen.respond.historyEmpty}
                  </p>
                ) : (
                  <ul className="support-detail-timeline">
                    {detail.responses.map((response) => (
                      <li
                        key={response.id}
                        className={`support-detail-entry ${
                          response.fromRequester ? "from-requester" : ""
                        }`}
                      >
                        <div className="support-detail-entry-header">
                          <span className="support-detail-entry-author">
                            {response.authorEmail}
                          </span>
                          <span className="support-detail-entry-date">
                            {formatDateTime(response.date)}
                          </span>
                        </div>
                        {response.status && (
                          <BoardDotStatus
                            pColor={getIncidentStatusColors(response.status).primary}
                            sColor={
                              getIncidentStatusColors(response.status).secondary
                            }
                            text={t.supportScreen.table.tbody[response.status]}
                          />
                        )}
                        {response.message && <p>{response.message}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {!isAdmin && detail && (
                <div className="support-detail-compose">
                  <h4 className="support-detail-subtitle">
                    {t.supportScreen.respond.replyTitle}
                  </h4>
                  {canReply ? (
                    <Edit orientation="vertical">
                      <Edit.TextArea
                        id="reply"
                        placeholder={t.supportScreen.respond.replyPlaceholder}
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                      />
                      {respondError && (
                        <p className="support-form-error">{respondError}</p>
                      )}
                    </Edit>
                  ) : (
                    <p className="support-detail-empty">
                      {t.supportScreen.respond.completedNoReply}
                    </p>
                  )}
                </div>
              )}

              {isAdmin && (
                <div className="support-detail-compose">
                  <h4 className="support-detail-subtitle">
                    {t.supportScreen.respond.newResponseTitle}
                  </h4>
                  <Edit orientation="vertical">
                    <Edit.Selection
                      id="status"
                      placeholder={t.supportScreen.respond.statusLabel}
                      items={itemsStatus}
                      selected={buildFilterObject(
                        "status",
                        { status: respondForm.status },
                        itemsStatus
                      )}
                      onSelect={(item) =>
                        setRespondForm({
                          ...respondForm,
                          status: item.key as IncidentStatusType,
                        })
                      }
                    />
                    <Edit.TextArea
                      id="message"
                      placeholder={t.supportScreen.respond.responseLabel}
                      value={respondForm.message}
                      onChange={(e) =>
                        setRespondForm({
                          ...respondForm,
                          message: e.target.value,
                        })
                      }
                    />
                    {respondError && (
                      <p className="support-form-error">{respondError}</p>
                    )}
                  </Edit>
                </div>
              )}
            </div>
          </Modal>
        </OperationArea>
      </Panel>
    </>
  );
};

export default SupportScreen;
